import { Log, LogConfig } from '@devanjs/log';
import { captureException, init } from '@sentry/vue';
import { isAxiosError } from 'axios';
import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import './tracer';
import './utils/dayjs';

import { billing } from '@teemill/modules/billing';
import { chat } from '@teemill/modules/chat';
import { drive } from '@teemill/modules/drive';
import { flows } from '@teemill/modules/flows';
import { gfn } from '@teemill/modules/gfn';
import { inserts } from '@teemill/modules/inserts';
import { mail } from '@teemill/modules/mail';
import { menu } from '@teemill/modules/menu';
import { modals } from '@teemill/modules/modals';

import { auth, useAuthToken } from '@/services/auth';
import { setup as billingSetup } from '@/services/billing';
import { integrations } from '@/services/integrations';
import { organisation } from '@/services/organisations';
import { platform } from '@/services/platform';
import { project, useProject } from '@/services/projects';
import { stripe } from '@/services/stripe';
import { user, useUser } from '@/services/user';

import { router } from '@/router';

import { integrations as availableIntegrations } from '@/integrations';

import { initDebug } from '@/debug';
import { authorize } from '@teemill/modules/authorize';
import { metrics } from '@teemill/modules/metrics';
import App from './App.vue';
import { ProjectNotFound } from './errors/projectNotFound';
import './index.css';
import { bookmarks } from './services/bookmarks';
import { apiBasePath } from './utils/apiBasePath';

LogConfig.environment = import.meta.env.MODE || 'production';
LogConfig.defaults('app', (log) =>
  log.if(import.meta.env.VITE_LOGGING_PLATFORM === 'true').blue('App'),
);
LogConfig.defaults('user', (log) =>
  log.if(import.meta.env.VITE_LOGGING_PROJECT === 'true').blue('User'),
);
LogConfig.defaults('project', (log) =>
  log.if(import.meta.env.VITE_LOGGING_PROJECT === 'true').blue('Project'),
);
LogConfig.defaults('integrations', (log) =>
  log.if(import.meta.env.VITE_LOGGING_INTEGRATIONS === 'true').blue('Integrations'),
);
LogConfig.defaults('mqtt', (log) =>
  log.if(import.meta.env.VITE_LOGGING_MQTT === 'true').purple('Mqtt'),
);

Log.blue('Environment').green(LogConfig.environment).info();

const pinia = createPinia();
const app = createApp(App);

init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.PROD ? 'production' : 'development',
  release: import.meta.env.VITE_APP_VERSION,
  tracesSampleRate: 1.0,
});

app
  .use(FloatingVue)
  .use(router)
  .use(stripe, { router })
  .use(pinia)
  .use(modals)
  .use(menu)
  .use(auth)
  .use(user, { router })
  .use(project, { router })
  .use(platform)
  .use(integrations, { router, integrations: availableIntegrations })
  .use(organisation)
  .use(billing, {
    setup: billingSetup,
    client: {
      basePath: apiBasePath(),
      accessToken: async () => useAuthToken(),
    },
    errorHandler: (error: unknown) => {
      captureException(error);
    },
  })
  .use(authorize, {
    client: {
      basePath: apiBasePath() + '/v1/enforcer',
      accessToken: async () => useAuthToken(),
    },
    user: () => useUser().active.value?.id,
    project: () => useProject().value?.id,
  })
  .use(drive, {
    client: {
      basePath: apiBasePath() + '/v1/drive',
      accessToken: async () => useAuthToken(),
    },
    errorHandler: (error: any) => {
      // const { client } = useDrive();

      // if (client.isResponseError(error) || error instanceof DriveError) {
      //   useNotifications().nudge(error.message, { level: 'danger' });
      //   return;
      // }

      captureException(error);
    },
  })
  .use(metrics, {
    metrics: {
      client: {
        basePath: apiBasePath() + '/v2/metrics',
        accessToken: async () => useAuthToken(),
      },
    },
    monitors: {
      client: {
        basePath: apiBasePath() + '/v1/metrics',
        accessToken: async () => useAuthToken(),
      },
    },
    errorHandler: (error: any) => {
      captureException(error);
    },
  })
  .use(chat, {
    client: {
      basePath: apiBasePath(),
      accessToken: async () => useAuthToken(),
    },
    errorHandler: (error: unknown) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;

      if (status !== undefined && [400, 422].includes(status)) {
        return;
      }

      captureException(error);
    },
  })
  .use(gfn, {
    client: {
      basePath: apiBasePath(),
      accessToken: async () => useAuthToken(),
    },
    errorHandler: (error) => {
      captureException(error);
    },
  })
  .use(flows, {
    client: {
      basePath: apiBasePath(),
      accessToken: async () => useAuthToken(),
    },
  })
  .use(mail, {
    client: {
      basePath: apiBasePath(),
      accessToken: async () => useAuthToken(),
    },
  })
  .use(inserts, {
    client: {
      basePath: apiBasePath(),
      accessToken: async () => useAuthToken(),
    },
  })
  .use(bookmarks);

// if (isFeatureEnabled('datadog')) {
//   app.use(datadog, {
//     applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
//     clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
//     version: import.meta.env.VITE_APP_VERSION,
//     service: import.meta.env.VITE_APP_NAME,
//     sampleRate: 50,
//   });
// }

app.config.errorHandler = (error, vm) => {
  if (error instanceof ProjectNotFound) {
    Log.tag('project').green('No Active').info('No project found in', vm);
  }

  captureException(error);
  throw error;
};

app.mount('#app');

initDebug();
